import React, { useCallback, useEffect, useRef, useState } from "react";
import { MOTION } from "../../styles/motionTokens";
import { prefersReducedMotion } from "../../styles/particleField";
import { Z, CHROME_INSET } from "../../styles/zLayers";
import { idStr, matchDirection } from "../tradeOpportunity";
import {
  cardsConnectCopy,
  collectorInitials,
  collectorMetaLine,
  collectorName,
  directionLabel,
} from "../matchRanking";
import MysteryCard from "./MysteryCard";
import { useDismissible } from "../../native/dismissibleStack";

/** Past this much horizontal travel a drag commits to the next match. */
const SWIPE_COMMIT_PX = 44;

/** How far the docked card protrudes above the sheet's top edge. */
const CARD_OVERHANG = 18;

const DECK_CARD_W = 62;
const DECK_CARD_H = 100;

/**
 * MatchDeck — the surface that owns selection once the trails have landed.
 *
 * It introduces the *collector*, not the cards: avatar, name, verified tick,
 * direction, how many cards connect the two collections, and the trust line.
 * Card names and art appear in exactly one place in this app, and it is not
 * here — the deck's whole job is to make opening the reveal feel worth doing.
 *
 * The docked mystery card protrudes over the sheet's top edge and uses the same
 * material as the map beacon, because that shared material is the only cue that
 * says "this is the card that just flew down off the map".
 */
const MatchDeck = ({
  open,
  queue = [],
  activeIndex = 0,
  exploredIds = [],
  cardVisible = true,
  anonymizeCollector = false,
  frame = "overview",
  onNext,
  onPrevious,
  onReveal,
  onToggleFrame,
  onHeightChange,
  onDismiss,
  /**
   * Attached to the docked card so the owner can measure the slot the flight
   * has to land in. Measured rather than derived from the deck's nominal height:
   * text scaling and a long collector name both move this slot, and a flight
   * that lands on a computed guess is the jitter people notice.
   */
  cardSlotRef,
}) => {
  const reduced = prefersReducedMotion();
  const sheetRef = useRef(null);
  const dragStartRef = useRef(null);

  const [dragDx, setDragDx] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [hasSwiped, setHasSwiped] = useState(false);
  const [tilt, setTilt] = useState(0);
  const [contentFading, setContentFading] = useState(false);
  const [narrow, setNarrow] = useState(false);

  const trader = queue[activeIndex] || null;
  const total = queue.length;
  const explored = new Set((exploredIds || []).map(idStr));
  const isExplored = trader ? explored.has(idStr(trader.id)) : false;

  // Android hardware Back cancels the match session rather than leaving the
  // map. Registered from in here, not from the owner's render, so the deck's
  // own render condition is the single source of truth about whether it can
  // claim Back — that condition already yields to the reveal, the profile
  // drawer and the trade modal, and a second copy of it at the call site would
  // drift. `trader` is in the condition for the same reason it is in the early
  // return below: with an empty queue there is no deck on screen to dismiss.
  useDismissible(Boolean(open && trader), onDismiss, "match-deck");

  // Report the measured height up so the camera can reserve it. Assuming a
  // height here is what puts the framed match behind the deck on the phones
  // whose text scaling makes this sheet taller than the mock.
  useEffect(() => {
    if (!open || !sheetRef.current || !onHeightChange) return undefined;
    const el = sheetRef.current;
    const report = () => onHeightChange(el.offsetHeight);
    report();
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(report);
    ro.observe(el);
    return () => ro.disconnect();
  }, [open, onHeightChange]);

  useEffect(() => {
    const check = () =>
      setNarrow(typeof window !== "undefined" && window.innerWidth < 380);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  // Crossfade the text on selection change so the old collector's name doesn't
  // hard-cut into the new one under the same avatar.
  useEffect(() => {
    if (reduced) return undefined;
    setContentFading(true);
    const id = window.setTimeout(() => setContentFading(false), 60);
    return () => window.clearTimeout(id);
  }, [activeIndex, reduced]);

  const settleTilt = useCallback(() => {
    if (reduced) return;
    window.setTimeout(() => setTilt(0), MOTION.matchSwipeMs);
  }, [reduced]);

  const commit = useCallback(
    (direction) => {
      setHasSwiped(true);
      setTilt(direction);
      settleTilt();
      if (direction > 0) onNext?.();
      else onPrevious?.();
    },
    [onNext, onPrevious, settleTilt]
  );

  /**
   * Horizontal position of a pointer event, or null if it carries none.
   *
   * Not every event path delivers `clientX` — synthetic events and some
   * non-mouse pointer sources arrive without it. Reading it blindly turns the
   * drag maths into NaN, which compares false against every threshold and
   * leaves the deck stuck mid-drag. Falling back and then refusing to start
   * a drag we can't measure is the honest behaviour: the arrows and the
   * keyboard still move the selection.
   */
  const pointerX = (e) => {
    const x = e?.clientX ?? e?.pageX ?? e?.touches?.[0]?.clientX;
    return Number.isFinite(x) ? x : null;
  };

  const onPointerDown = (e) => {
    // A press that starts on a control is that control's, not a swipe.
    if (e.target?.closest?.("button")) return;
    const x = pointerX(e);
    if (x === null) return;
    dragStartRef.current = x;
    setDragging(true);
  };

  const onPointerMove = (e) => {
    if (dragStartRef.current === null) return;
    const x = pointerX(e);
    if (x === null) return;
    setDragDx(x - dragStartRef.current);
  };

  const endDrag = (e) => {
    if (dragStartRef.current === null) return;
    const x = pointerX(e);
    const dx = x === null ? 0 : x - dragStartRef.current;
    dragStartRef.current = null;
    setDragging(false);
    setDragDx(0);
    if (Math.abs(dx) < SWIPE_COMMIT_PX) {
      if (Math.abs(dx) > 6) setHasSwiped(true);
      return;
    }
    commit(dx < 0 ? 1 : -1);
  };

  const cancelDrag = () => {
    dragStartRef.current = null;
    setDragging(false);
    setDragDx(0);
  };

  const onKeyDown = (e) => {
    if (total < 2) return;
    if (e.key === "ArrowRight") {
      e.preventDefault();
      commit(1);
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      commit(-1);
    }
  };

  if (!open || !trader) return null;

  const multi = total > 1;
  const direction = matchDirection(trader);
  const eyebrow =
    explored.size > 0
      ? `Match ${activeIndex + 1} of ${total} · ${explored.size} explored`
      : `Match ${activeIndex + 1} of ${total}`;

  const cardTilt = `translateX(${Math.round(dragDx * 0.55)}px) rotateX(-7deg) rotateY(${
    4 + (dragging ? dragDx * 0.06 : tilt * 6)
  }deg)`;

  const contentOpacity = contentFading ? 0.25 : 1;
  const contentStyle = {
    opacity: contentOpacity,
    // The crossfade between two matches is decoration over a swap that has
    // already happened, so under reduced motion the new match is simply there.
    transition: reduced ? "none" : "opacity 200ms ease",
  };

  return (
    <div
      ref={sheetRef}
      role="group"
      aria-label="Match deck"
      data-testid="match-deck"
      tabIndex={-1}
      onKeyDown={onKeyDown}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={cancelDrag}
      style={{
        position: "absolute",
        left: 10,
        right: 10,
        bottom: `calc(${CHROME_INSET.bottom + 14}px + var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)))`,
        // Map furniture, not an overlay: the deck never dims the app behind it,
        // so it sits above the map's own controls and below every portalled
        // sheet. At `modalContent` it outranked MUI's drawer and modal layers
        // and ate the taps meant for the profile sheet and the Trade Arena.
        zIndex: Z.mapPanel,
        background:
          "linear-gradient(180deg, rgba(255,255,255,0.98), rgba(249,246,255,0.98))",
        backdropFilter: "blur(18px)",
        borderRadius: 24,
        borderTop: "1px solid rgba(255,255,255,0.9)",
        // One broad upward shadow for the whole sheet — no per-child shadows,
        // or the deck stops reading as a single slab of material.
        boxShadow:
          "0 -20px 44px -16px rgba(38,17,84,0.42), 0 0 0 1px rgba(109,40,217,0.09)",
        padding: "9px 15px 15px",
        animation: reduced
          ? "none"
          : `matchDeckUp ${MOTION.sheetSlideMs}ms cubic-bezier(0.2,0.8,0.2,1)`,
        touchAction: "pan-y",
        maxWidth: 520,
        margin: "0 auto",
      }}
    >
      <div
        style={{
          width: 36,
          height: 4,
          borderRadius: 2,
          background: "#e2e0ea",
          margin: "0 auto 9px",
        }}
      />

      <div style={{ position: "relative", display: "flex", gap: 14, alignItems: "flex-start" }}>
        {/* Docked card slot. The recess stays even while the card is in flight,
            so nothing reflows when it lands. */}
        <div style={{ flex: "0 0 auto", width: DECK_CARD_W, height: 56, position: "relative" }}>
          <span
            style={{
              position: "absolute",
              left: -8,
              right: -8,
              bottom: -7,
              height: 62,
              borderRadius: 15,
              background: "rgba(76,29,149,0.055)",
              boxShadow: "inset 0 2px 6px rgba(46,16,101,0.1)",
            }}
          />
          <div
            ref={cardSlotRef}
            data-testid="match-deck-card"
            style={{
              position: "absolute",
              left: 0,
              bottom: -4,
              width: DECK_CARD_W,
              height: DECK_CARD_H,
              perspective: 700,
              opacity: cardVisible ? 1 : 0,
              transition: reduced ? "none" : "opacity 200ms ease",
            }}
          >
            <div
              style={{
                position: "absolute",
                inset: 0,
                transformStyle: "preserve-3d",
                transform: cardTilt,
                // Off during a drag because the card has to stay under the
                // finger, and off under reduced motion because the swipe
                // settle is the largest movement this surface makes. The tilt
                // itself still updates either way.
                transition:
                  dragging || reduced
                    ? "none"
                    : `transform ${MOTION.matchSwipeMs}ms cubic-bezier(0.2,0.8,0.2,1)`,
              }}
            >
              <MysteryCard
                width={DECK_CARD_W}
                height={DECK_CARD_H}
                radius={10}
                glyphSize={24}
                sheen={!reduced}
                explored={isExplored}
              />
            </div>
          </div>
        </div>

        <div style={{ minWidth: 0, flex: 1, paddingTop: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
            <span
              data-testid="match-deck-eyebrow"
              style={{
                minWidth: 0,
                flex: 1,
                fontSize: 9.5,
                fontWeight: 800,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                color: "#7c3aed",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {eyebrow}
            </span>
            {multi && (
              <button
                type="button"
                data-testid="match-deck-frame-toggle"
                onClick={onToggleFrame}
                // The chip READS as a 30px secondary control, but a 30px touch
                // target does not work on a phone. The button is a transparent
                // 44px-tall wrapper and the pill inside carries the visual, so
                // the tappable area is compliant without moving a pixel of the
                // design.
                style={{
                  flex: "0 0 auto",
                  display: "flex",
                  alignItems: "center",
                  minHeight: 44,
                  padding: 0,
                  border: 0,
                  background: "transparent",
                  cursor: "pointer",
                  fontFamily: "inherit",
                }}
              >
                <span
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                    height: 30,
                    padding: "0 10px 0 8px",
                    border: "1px solid rgba(15,23,42,0.08)",
                    borderRadius: 15,
                    background: "rgba(255,255,255,0.7)",
                    color: "#5b6474",
                    fontSize: 11,
                    fontWeight: 600,
                  }}
                >
                  {frame === "overview" ? "Focus match" : `View all ${total}`}
                </span>
              </button>
            )}
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 7, ...contentStyle }}>
            <span
              aria-hidden="true"
              style={{
                flex: "0 0 auto",
                width: 26,
                height: 26,
                borderRadius: "50%",
                background: anonymizeCollector
                  ? "linear-gradient(150deg,#a29cc8,#645a9c)"
                  : "linear-gradient(150deg,#a78bfa,#6d28d9)",
                display: "grid",
                placeItems: "center",
                color: "#fff",
                fontSize: 10,
                fontWeight: 800,
                boxShadow: "0 3px 8px -2px rgba(46,16,101,0.5)",
              }}
            >
              {collectorInitials(trader, { anonymize: anonymizeCollector })}
            </span>
            <span
              data-testid="match-deck-collector"
              style={{
                minWidth: 0,
                fontSize: 13.5,
                fontWeight: 700,
                color: "#100c1c",
                letterSpacing: "-0.01em",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {collectorName(trader, { anonymize: anonymizeCollector })}
            </span>
            {!!trader.verified && !anonymizeCollector && (
              <span
                title="Verified collector"
                style={{ flex: "0 0 auto", display: "flex", alignItems: "center", color: "#2563eb" }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M12 2l2.4 1.8 3-.2.9 2.9 2.5 1.7-1.2 2.8 1.2 2.8-2.5 1.7-.9 2.9-3-.2L12 22l-2.4-1.8-3 .2-.9-2.9L3.2 15.8 4.4 13 3.2 10.2l2.5-1.7.9-2.9 3 .2L12 2zm-1 13.2 5-5-1.4-1.4-3.6 3.6-1.6-1.6L8 12.2l3 3z" />
                </svg>
                <span style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clipPath: "inset(50%)" }}>
                  Verified
                </span>
              </span>
            )}
          </div>

          {/* Direction is text, never colour alone. */}
          <div
            data-testid="match-deck-direction"
            style={{
              display: "inline-flex",
              alignItems: "center",
              height: 22,
              padding: "0 9px",
              borderRadius: 11,
              background: direction === "mutual" ? "rgba(109,40,217,0.11)" : "rgba(16,12,28,0.06)",
              color: direction === "mutual" ? "#5b21b6" : "#4b465e",
              fontSize: 10.5,
              fontWeight: 700,
              marginBottom: 7,
              ...contentStyle,
            }}
          >
            {directionLabel(trader, { narrow })}
          </div>

          <div
            data-testid="match-deck-cards-copy"
            style={{
              fontSize: 15.5,
              fontWeight: 700,
              color: "#100c1c",
              lineHeight: 1.25,
              letterSpacing: "-0.02em",
              ...contentStyle,
            }}
          >
            {cardsConnectCopy(trader)}
          </div>
          <div
            data-testid="match-deck-meta"
            style={{ fontSize: 11.5, color: "#6f6a85", marginTop: 5, ...contentStyle }}
          >
            {collectorMetaLine(trader, { anonymize: anonymizeCollector })}
          </div>
        </div>
      </div>

      {multi && !hasSwiped && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 6,
            marginTop: 11,
            fontSize: 10.5,
            fontWeight: 600,
            color: "#8b84a3",
          }}
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M5 12h14M5 12l4-4M5 12l4 4M19 12l-4-4M19 12l-4 4" />
          </svg>
          Swipe to explore matches
        </div>
      )}

      <div style={{ display: "flex", gap: 8, marginTop: multi && !hasSwiped ? 10 : 14 }}>
        {multi && (
          <button
            type="button"
            aria-label="Previous match"
            data-testid="match-deck-prev"
            onClick={() => commit(-1)}
            style={navButtonStyle}
          >
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
        )}
        <button
          type="button"
          data-testid="match-deck-reveal"
          onClick={() => onReveal?.(trader)}
          style={{
            flex: 1,
            minHeight: 46,
            border: 0,
            borderRadius: 14,
            background: "linear-gradient(145deg,#8b5cf6,#6d28d9)",
            color: "#fff",
            fontSize: 13.5,
            fontWeight: 700,
            letterSpacing: "-0.01em",
            cursor: "pointer",
            boxShadow: "0 12px 26px -10px rgba(109,40,217,0.9)",
            fontFamily: "inherit",
          }}
        >
          {isExplored ? "See this match again" : "Reveal this match"}
        </button>
        {multi && (
          <button
            type="button"
            aria-label="Next match"
            data-testid="match-deck-next"
            onClick={() => commit(1)}
            style={navButtonStyle}
          >
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M9 6l6 6-6 6" />
            </svg>
          </button>
        )}
        {!multi && (
          <button
            type="button"
            aria-label="Close match deck"
            data-testid="match-deck-dismiss"
            onClick={onDismiss}
            style={navButtonStyle}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" aria-hidden="true">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      <style>{`
        @keyframes matchDeckUp {
          from { transform: translateY(116%); }
          to   { transform: none; }
        }
      `}</style>
    </div>
  );
};

/** ≥44×44 hit area, the app's own control chrome. */
const navButtonStyle = {
  width: 46,
  minHeight: 46,
  border: "1px solid rgba(15,23,42,0.09)",
  borderRadius: 14,
  background: "rgba(255,255,255,0.8)",
  color: "#3f3a52",
  cursor: "pointer",
  display: "grid",
  placeItems: "center",
  fontFamily: "inherit",
};

export { CARD_OVERHANG, DECK_CARD_W, DECK_CARD_H, SWIPE_COMMIT_PX };
export default MatchDeck;
